/**
 * Vault selection keys are `file-<uid>`, `contract-<uid>` and `dir-<uid>`.
 * Every bulk endpoint takes the three kinds as separate uid lists.
 */
export interface VaultUidGroups {
  fileUids: string[];
  contractUids: string[];
  directoryUids: string[];
}

const PREFIXES = {
  fileUids: 'file-',
  contractUids: 'contract-',
  directoryUids: 'dir-',
} as const;

export function groupVaultKeys(keys: readonly string[]): VaultUidGroups {
  const groups: VaultUidGroups = { fileUids: [], contractUids: [], directoryUids: [] };
  for (const key of keys) {
    for (const [group, prefix] of Object.entries(PREFIXES) as [keyof VaultUidGroups, string][]) {
      if (key.startsWith(prefix)) groups[group].push(key.slice(prefix.length));
    }
  }
  return groups;
}

export function isEmptyVaultGroups(groups: VaultUidGroups): boolean {
  return (
    groups.fileUids.length === 0 &&
    groups.contractUids.length === 0 &&
    groups.directoryUids.length === 0
  );
}

/** "2 files, 1 contract, 3 folders" — the kinds present, in listing order reversed to files first. */
export function describeVaultCounts(counts: {
  files: number;
  contracts: number;
  folders: number;
}): string {
  const parts: string[] = [];
  if (counts.files > 0) parts.push(`${counts.files} ${counts.files === 1 ? 'file' : 'files'}`);
  if (counts.contracts > 0) {
    parts.push(`${counts.contracts} ${counts.contracts === 1 ? 'contract' : 'contracts'}`);
  }
  if (counts.folders > 0) {
    parts.push(`${counts.folders} ${counts.folders === 1 ? 'folder' : 'folders'}`);
  }
  return parts.join(', ');
}
